import Icon from '@components/atoms/icon/icon';
import NotificationsLoadingAnimationV2 from '@components/notifications-v2/components/notification-loading-animation-v2/notification-loading-animation-v2';
import { User } from '@interfaces/models/user';
import { useChat } from '@context/chat.context';
import { UseInfiniteQueryResult } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'next-i18next';
import { usePreferences } from '@context/preferences.context';
import useChatHistory from '@hooks/chat/use-chat-history';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import logger from '@helpers/utils/logger/client';
import { getBuyerInfoFromChannel } from '@helpers/chat-helpers';
import { debounce } from '@helpers/utils/general';
import { formatDate, formatDateTime, formatTime } from '@helpers/utils/date-time-formatter-helper';
import sanitizer from '@helpers/sanitizer';
import { isDefined } from '@helpers/utils/type-utils';
import useUser from '@hooks/user/use-user';
import Link from 'next/link';
import ProfilePicture from '@components/molecules/profile/profile-picture';
import styles from './chat-content.module.scss';
import ChatBanModal, { EnumBanType } from './chat-ban-modal/chat-ban-modal';

export interface ChatContentProps {
  isSeller?: boolean;
  sellerInfo?: User;
  userId?: string;
  chatFromPdp?: boolean;
  openChatFromLike?: boolean;
  openChatOnBoardingModal?: Function;
  reloadChat?: () => void;
}

const ChatContent: React.FC<ChatContentProps> = (props) => {
  const {
    isSeller = false,
    sellerInfo,
    userId,
    chatFromPdp = false,
    openChatFromLike = false,
    openChatOnBoardingModal = () => {},
    reloadChat,
  } = props;

  const {
    chatWrapper,
    markAsRead,
    translateMessage,
    messages,
    setMessages,
    msgBeenRead,
    setMsgBeenRead,
    transformMessages,
    messagesEnding,
    scrollToBottom,
    tempStatus,
    handleSendingMessage,
    isLoading,
    setIsLoading,
    channel,
    senderBan,
    receiverBan,
    mode,
    channelError,
    tokenError,
  } = useChat();

  const { isFetching, isFetchingNextPage, fetchNextPage, hasNextPage, updater } = useChatHistory(25);
  const { sendAnalyticEvent: sendChatAnalyticEvents } = useAnalyticEvents('chat');
  const { t } = useTranslation();
  const { t: tA11y } = useTranslation('a11y');
  const { language } = usePreferences();
  const translateLanguageMapping = {
    hk: 'zh-TW',
    us: 'en',
  };

  const translationLanguage = translateLanguageMapping[language] || language;
  const isInline = mode === 'inline';

  const { user } = useUser();

  const chatContent = useRef(null);
  const restrictedInfo = isSeller ? getBuyerInfoFromChannel(channel) : sellerInfo;
  const [messagesDeletedByModeration, setMessagesDeletedByModeration] = useState<string[]>([]);
  const [isBanModalOpen, setIsBanModalOpen] = useState<boolean>(false);

  const lastMsgRead = () => {
    const channelStateRead = channel?.state?.read;
    setMsgBeenRead(
      Object.keys(channelStateRead).some((id) => userId && id !== userId && channelStateRead[id].unread_messages === 0),
    );
  };

  const channelHandler = (event) => {
    switch (event.type) {
      case 'message.new':
        if (event.message.user.id !== userId) {
          translateMessage(event.message.id, translationLanguage)
            .then(() => {
              setMessages(transformMessages(channel.state.messages));
              markAsRead(channel).catch((err) => {
                logger.error(err);
              });
              scrollToBottom(mode);
            })
            .catch(() => setMessages(transformMessages(channel.state.messages)));
        } else {
          setMessages(transformMessages(channel.state.messages));
          scrollToBottom(mode);
        }
        if (isInline) {
          updater((oldData: UseInfiniteQueryResult) => {
            const newData: UseInfiniteQueryResult = { ...oldData };
            const curMsg = channel?.state?.messages[channel?.state?.messages?.length - 1];
            // @ts-ignore
            newData?.pages[0]?.messages?.push(curMsg);
            return newData;
          });
        }
        break;
      case 'message.read':
        if (event.user && event.user.id !== userId) {
          setMsgBeenRead(true);
        }
        break;
      case 'message.deleted':
        setMessagesDeletedByModeration((prevMessagesDeletedByModeration) => [
          ...prevMessagesDeletedByModeration,
          event.message.id,
        ]);
        break;
      default:
        break;
    }
  };

  const getMessagesFromChannel = () => {
    setIsLoading(true);
    Promise.all(
      channel.state.messages.map((msg) => {
        return msg.i18n && msg.i18n[`${translationLanguage}_text`]
          ? Promise.resolve(msg)
          : translateMessage(msg.id, translationLanguage).catch((err) => {
              logger.info(err);
            });
      }),
    )
      .then(() => {
        // eslint-disable-next-line no-return-assign
        channel.state.messages.map((message) => (message.status = 'received'));
        const newMsgs = transformMessages([...channel.state.messages]);
        setMessages(newMsgs);
        setIsLoading(false);
        scrollToBottom(mode);
      })
      .catch((err) => {
        setIsLoading(false);
        logger.error(err);
      });
  };

  const retryToSend = (id: string) => {
    if (channelError) {
      return;
    }
    const latestMessages = messages[messages.length - 1].value;
    const index = latestMessages.findIndex((e) => e.id === id);
    if (latestMessages[index] && latestMessages[index].status === tempStatus.NO_NETWORK) {
      const { text } = latestMessages[index];
      latestMessages.splice(index, 1);
      setMessages([...messages]);
      handleSendingMessage(channel, text);
    }
  };

  const HandleScroll = (e) => {
    if (hasNextPage && !isFetchingNextPage && e.target.scrollTop < 120) {
      fetchNextPage();
    }
  };

  const hasSenderBan = senderBan.channel.banned || senderBan.global.banned;

  const closePopUp = (): void => {
    setIsBanModalOpen(false);
  };

  const getBanFormat = (): EnumBanType => {
    if (!hasSenderBan) {
      return EnumBanType.LINK_BLOCK;
    }
    switch (senderBan.global.banDuration) {
      case undefined:
      case null:
        return EnumBanType.PERMA_BAN;
      default:
        return EnumBanType.TEMP_BAN;
    }
  };

  useEffect(() => {
    if (channel) {
      channel.on(channelHandler);
      markAsRead(channel)
        .then(() => {
          setIsLoading(false);
          scrollToBottom(mode);
        })
        .catch((err) => {
          setIsLoading(false);
          logger.error(err);
        });
      lastMsgRead();
      if (!isInline) {
        getMessagesFromChannel();
      }
    }
  }, [channel]);

  const openChatBanModal = (): void => {
    if (hasSenderBan) {
      setIsBanModalOpen(true);
    }
  };

  useEffect(() => {
    if (senderBan.global.banned === false) {
      closePopUp();
    }
  }, [senderBan.global.banned]);

  const buyerImg = () => {
    const profileString = '/profil/';
    const rawImgUrl = restrictedInfo.picture.path;
    const urlStringArray = rawImgUrl?.split(profileString) || [];
    return urlStringArray.length > 0 ? `${profileString}${urlStringArray[1]}` : rawImgUrl;
  };
  const isUserSeller = userId === sellerInfo?.id;
  return (
    <div
      className={clsx(styles.chatContent, { hasLoader: isLoading }, isInline && styles.chatContent__inline)}
      tabIndex={-1}
    >
      {/* CHAT WRAPPER */}
      {(channelError || tokenError) && (
        <div className={clsx(styles.chatContent__errorBanner)}>
          <Trans i18nKey="CHAT.ERRORBANNER.MESSAGE">
            Oops! Something went wrong. NEED TRANSLATE
            <button
              className="vc-link"
              onClick={reloadChat}
            >
              Try again
            </button>
          </Trans>
        </div>
      )}
      <div
        ref={chatWrapper}
        role="log"
        aria-relevant="additions"
        aria-live="polite"
        aria-atomic="false"
      >
        <ul
          className={clsx(styles.chat, { [styles.chat__fromPdp]: chatFromPdp })}
          onScroll={isInline ? debounce(HandleScroll) : undefined}
        >
          {/* Vestiaire Collective disclaimer */}
          <li
            ref={chatContent}
            className={styles.msgChat}
          >
            <span className={clsx(styles.msgChat__pic, styles.msgChat__pic__disclaimer)}>
              <Icon
                name="logo-v"
                className={styles.msgChat__icon}
                ariaLabel={tA11y('A11Y.IMAGE.VC_AVATAR')}
              />
            </span>
            <p
              className={clsx(styles.msgChat__content__item, styles.msgChat__content__item__disclaimer, 'vc-text-s')}
              data-cy="chat_disclaimer"
            >
              <Trans
                t={t}
                i18nKey="CHAT.CONTENT.DISCLAIMER"
              >
                We will never request financial details or other sensitive data via chat. For your safety, do not share
                personal information.
                <button
                  className="vc-link"
                  onClick={(e) => {
                    e.preventDefault();
                    openChatOnBoardingModal();
                  }}
                >
                  Learn more
                </button>
                But if you have problems with another member, please use the
                <a
                  className="vc-link"
                  href={t('CHAT.CONTENT.DISCLAIMER.LINK_CONTACT')}
                  onClick={() => {
                    sendChatAnalyticEvents('bs_chat_report_conversation');
                  }}
                  data-cy="chat_report_function"
                >
                  the report function
                </a>
                .
              </Trans>
            </p>
          </li>

          {isFetching && <NotificationsLoadingAnimationV2 />}

          {/* First msg: introduction of the seller (default case) */}
          {!isSeller && (
            <li className={styles.msgChat}>
              <Link
                className={styles.msgChat__introBuyer}
                href={`/profile/${sellerInfo?.id}`}
              >
                <ProfilePicture
                  className={styles.msgChat__pic}
                  picturePath={sellerInfo?.picture.path}
                  isUserProfile={isUserSeller}
                  userId={sellerInfo?.id}
                  width={60}
                  height={60}
                  alt={tA11y('A11Y.IMAGE.SELLER_AVATAR')}
                  disableLazyLoad
                />
              </Link>
              {sellerInfo && (
                <div className={clsx(styles.msgChat__content, 'vc-text-l')}>
                  <Link
                    className={styles.msgChat__introBuyer}
                    href={`/profile/${sellerInfo?.id}`}
                  >
                    <p className={styles.msgChat__content__item}>
                      {t('CHAT.CONTENT.INTRO_SELLER', {
                        receiverInfo: sellerInfo,
                      })}
                    </p>
                  </Link>
                </div>
              )}
            </li>
          )}
          {/* End first msg intro */}

          {/* First msg: introduction of the buyer (like-notification case) */}
          {openChatFromLike && isSeller && (
            <li className={clsx(styles.msgChat, styles.msgChat__introBuyer)}>
              <Link
                className={styles.msgChat__introBuyer}
                href={`/profile/${sellerInfo?.id}`}
              >
                <ProfilePicture
                  className={styles.msgChat__pic}
                  picturePath={sellerInfo?.picture.path}
                  isUserProfile={isUserSeller}
                  userId={sellerInfo?.id}
                  width={60}
                  height={60}
                  alt={tA11y('A11Y.IMAGE.SELLER_AVATAR')}
                  disableLazyLoad
                />
                {sellerInfo && (
                  <div className={clsx(styles.msgChat__content, 'vc-text-s')}>
                    <p className={styles.msgChat__content__case}>
                      {t('CHAT.CONTENT.INTRO_BUYER.LIKE', {
                        receiverInfo: sellerInfo,
                      })}
                    </p>
                    <p>{t('CHAT.CONTENT.INTRO_BUYER.SUGG')}</p>
                  </div>
                )}
              </Link>
            </li>
          )}
          {/* End first msg intro */}

          {/* Beginning of discussion */}
          {messages?.map((message, messagesIndex) => (
            <React.Fragment key={message.key}>
              <li className={clsx(styles.dateChat, 'vc-text-xs')}>
                {formatDateTime(new Date(message.key), user?.countryISO)}
              </li>
              {message?.value?.map((item, index) => (
                <li
                  key={item.id}
                  className={clsx(
                    styles.msgChat,
                    {
                      [styles.msgChat__sender]: item.user.id === userId,
                    },
                    'msgChat__item',
                  )}
                >
                  {/* PICTURE DISPLAY */}
                  {userId !== item.user.id && (
                    <Link
                      className={styles.msgChat__pic__anchor}
                      href={`/profile/${item?.user?.id}`}
                      onClick={() => {
                        sendChatAnalyticEvents('members_profile_click');
                      }}
                    >
                      <ProfilePicture
                        className={styles.msgChat__pic}
                        picturePath={buyerImg()}
                        isUserProfile={item?.user?.id === userId}
                        userId={item?.user?.id}
                        width={60}
                        height={60}
                        alt={tA11y('A11Y.IMAGE.SELLER_AVATAR')}
                        disableLazyLoad
                      />
                    </Link>
                  )}

                  {/* CONTENT DISPLAY */}
                  <div
                    className={clsx(styles.msgChat__content, 'vc-text-l')}
                    onClick={() => retryToSend(item.id)}
                    onKeyDown={() => retryToSend(item.id)}
                    role={item.status === tempStatus.NO_NETWORK ? 'button' : undefined}
                    tabIndex={item.status === tempStatus.NO_NETWORK ? 0 : -1}
                  >
                    <div className={styles.msgChat__content__container}>
                      {(item.rewritten_by_presend_hook ||
                        messagesDeletedByModeration.includes(item.id) ||
                        item.type === 'deleted') && (
                        <p
                          className={clsx(styles.msgChat__content__item, styles.msgChat__content__item__abusive)}
                          data-cy="chat_msg_deleted"
                        >
                          {t('CHAT.CONTENT.MSG_DELETED')}
                        </p>
                      )}
                      {!item.rewritten_by_presend_hook &&
                        !messagesDeletedByModeration.includes(item.id) &&
                        item.type !== 'deleted' && (
                          <p
                            className={clsx(
                              styles.msgChat__content__item,
                              {
                                [styles.msgChat__content__failed]:
                                  item.status === tempStatus.BANNED ||
                                  item.status === tempStatus.LINK ||
                                  item.status === tempStatus.NO_NETWORK,
                              },
                              {
                                [styles.msgChat__content__item__sender]: userId === item.user.id,
                              },
                            )}
                            data-cy={userId === item.user.id ? 'chat_msg_sent' : 'chat_msg_received'}
                          >
                            {userId !== item.user.id
                              ? (item.i18n && item.i18n[`${translationLanguage}_text`]) || item.text
                              : item.text}
                          </p>
                        )}

                      {(item.status === tempStatus.BANNED ||
                        item.status === tempStatus.LINK ||
                        item.status === tempStatus.NO_NETWORK) && (
                        <span className={styles.msgChat__content__container__infoIcon}>i</span>
                      )}
                    </div>

                    {/* MESSAGE WARNING */}
                    {userId === item.user.id && (
                      <div>
                        {/* /status msg = sending */}
                        {item.status === tempStatus.SENDING && (
                          <p className={clsx(styles.msgChat__info, 'vc-text-s')}>{t('CHAT.CONTENT.MSG_SENDING')}...</p>
                        )}

                        {/* /status msg = send */}
                        {!msgBeenRead &&
                          messagesIndex === messages.length - 1 &&
                          index === message.value.length - 1 &&
                          item.status === 'received' &&
                          item.type !== 'deleted' &&
                          !item.rewritten_by_presend_hook && (
                            <p className={clsx(styles.msgChat__info, 'vc-text-xs')}>{t('CHAT.CONTENT.MSG_SENT')}</p>
                          )}

                        {/* /status msg = seen */}
                        {msgBeenRead &&
                          messagesIndex === messages.length - 1 &&
                          index === message.value.length - 1 &&
                          item.status === 'received' &&
                          item.type !== 'deleted' &&
                          !item.rewritten_by_presend_hook && (
                            <p className={clsx(styles.msgChat__info, 'vc-text-xs')}>{t('CHAT.CONTENT.MSG_SEEN')}</p>
                          )}

                        {/* /low connection case */}
                        {item.status === tempStatus.NO_NETWORK && (
                          <p className={clsx(styles.msgChat__info, styles.msgChat__info__failed, 'vc-text-s')}>
                            {t('CHAT.CONTENT.MSG_FAILED')}
                          </p>
                        )}

                        {/* User try to send a link */}
                        {item.status === tempStatus.LINK && (
                          <p className={clsx(styles.msgChat__info, styles.msgChat__info__failed, 'vc-text-s')}>
                            <Trans
                              t={t}
                              i18nKey="CHAT.CONTENT.MSG_ERROR_LINK"
                            >
                              Message blocked. Sending links is not allowed.
                              <button
                                className="vc-link"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setIsBanModalOpen(true);
                                }}
                                data-cy="chat_message_link_btn"
                              >
                                Learn more
                              </button>
                            </Trans>
                          </p>
                        )}

                        {/* User is banned and has not refresh the page yet */}
                        {item.status === tempStatus.BANNED && (
                          <p className={clsx(styles.msgChat__info, styles.msgChat__info__failed, 'vc-text-s')}>
                            <Trans
                              t={t}
                              i18nKey="CHAT.CONTENT.MSG_ERROR_BAN"
                            >
                              Message blocked because you no longer have access to chat
                              <button
                                className="vc-link"
                                onClick={(e) => {
                                  e.preventDefault();
                                  location.reload();
                                }}
                              >
                                Learn more
                              </button>
                            </Trans>
                          </p>
                        )}

                        {/* /abusive case */}
                        {(item.rewritten_by_presend_hook ||
                          messagesDeletedByModeration.includes(item.id) ||
                          item.type === 'deleted') && (
                          <div className={clsx(styles.msgChat__info, styles.msgChat__info__abusive, 'vc-text-m')}>
                            <p>
                              <Trans i18nKey="CHAT.CONTENT.MSG_FLAGGED_WARNING_SENT">
                                Violate chat rules
                                <button
                                  className="vc-link"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    openChatOnBoardingModal(true);
                                  }}
                                  data-cy="chat_flagged_btn"
                                >
                                  Learn more
                                </button>
                              </Trans>
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {userId !== item.user.id && item.rewritten_by_presend_hook && (
                      <div
                        className={clsx(
                          styles.msgChat__info,
                          styles.msgChat__info__abusive,
                          styles.msgChat__info__abusive__received,
                          'vc-text-m',
                        )}
                      >
                        <p>
                          <Trans i18nKey="CHAT.CONTENT.MSG_FLAGGED_WARNING_RECEIVED">
                            This user sent a message which does not comply with our
                            <a
                              href={t('CHAT.CONTENT.MSG_FLAGGED_WARNING.LINK')}
                              onClick={() => {
                                sendChatAnalyticEvents('bs_chat_see_terms');
                              }}
                            >
                              {' '}
                              terms.{' '}
                            </a>
                            Keep your personal info safe; only purchase through Vestiaire.
                          </Trans>
                        </p>
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </React.Fragment>
          ))}
          {sellerInfo?.language?.code !== language && (
            <li className={styles.chat__translation}>
              <span className="vc-text-s">{t('CHAT.CONTENT.TRANSLATE_CONVERSATION')}</span>
            </li>
          )}
          <div ref={messagesEnding} />
        </ul>
      </div>

      {/* BLOCKING WARNING */}
      {receiverBan.channel.banned && !receiverBan.global.banned && !hasSenderBan && (
        <p
          data-cy="blocked-receiver-bnr"
          className={clsx(styles.beingBlocked, isInline && styles.beingBlocked__inline)}
        >
          {restrictedInfo?.username && (
            <span className={clsx(styles.beingBlocked__info, 'vc-text-m')}>
              {t('CHAT.FORM.INTERLOCUTOR_BLOCKED', {
                receiverInfo: restrictedInfo,
              })}
            </span>
          )}
          <span className={clsx(styles.beingBlocked__csq, 'vc-text-m')}>{t('CHAT.FORM.INTERLOCUTOR_BLOCKED.CSQ')}</span>
        </p>
      )}

      {senderBan.channel.banned && !senderBan.global.banned && (
        <p
          data-cy="blocked-sender-bnr"
          className={clsx(styles.beingBlocked, isInline && styles.beingBlocked__inline)}
        >
          {restrictedInfo?.username && (
            <span className={clsx(styles.beingBlocked__info, 'vc-text-m')}>
              {t('CHAT.FORM.YOURSELF_BLOCKED', { receiverInfo: restrictedInfo })}
            </span>
          )}
          <span className={clsx(styles.beingBlocked__csq, 'vc-text-m')}>{t('CHAT.FORM.YOURSELF_BLOCKED.CSQ')}</span>
        </p>
      )}

      {/* BANNING WARNING */}
      {receiverBan.global.banned && !hasSenderBan && (
        <p
          data-cy="banned-receiver-bnr"
          className={clsx(styles.beingBlocked, isInline && styles.beingBlocked__inline)}
        >
          {restrictedInfo?.username && (
            <span className={clsx(styles.beingBlocked__info, 'vc-text-m')}>
              {t('CHAT.FORM.INTERLOCUTOR_BANNED', { receiverInfo: restrictedInfo })}
            </span>
          )}
          <span className={clsx(styles.beingBlocked__csq, 'vc-text-m')}>{t('CHAT.FORM.INTERLOCUTOR_BANNED.CSQ')}</span>
        </p>
      )}

      {senderBan.global.banned && (
        <p
          data-cy="banned-sender-bnr"
          className={clsx(styles.beingBlocked, isInline && styles.beingBlocked__inline)}
        >
          {user && isDefined(senderBan.global.banDuration) ? (
            <span
              className={clsx(styles.beingBlocked__info, 'vc-text-m')}
              dangerouslySetInnerHTML={{
                __html: sanitizer(
                  t('CHAT.FORM.YOURSELF_BANNED.DURATION', {
                    duration: senderBan.global.banDuration,
                    endTime: formatTime(senderBan.global.expiry, user.countryISO),
                    endDate: formatDate(senderBan.global.expiry, false, user.countryISO),
                  }),
                ),
              }}
            />
          ) : (
            <span
              className={clsx(styles.beingBlocked__info, 'vc-text-m')}
              dangerouslySetInnerHTML={{
                __html: sanitizer(t('CHAT.FORM.YOURSELF_BANNED')),
              }}
            />
          )}
          &nbsp;
          <button
            onClick={openChatBanModal}
            className={clsx(styles.beingBlocked__csq, 'vc-text-m vc-link')}
          >
            {t('CHAT.FORM.YOURSELF_BANNED.CSQ')}
          </button>
        </p>
      )}
      <ChatBanModal
        banEndTime={isDefined(senderBan.global.banDuration) ? senderBan.global.expiry : null}
        banType={getBanFormat()}
        isOpen={isBanModalOpen}
        onClose={closePopUp}
      />
    </div>
  );
};

export default ChatContent;
